<script setup lang="ts">
import { useQuasar } from 'quasar';
import { computed, onMounted } from 'vue';
import router from '@/router';
import type { Locales, ProjectModuleDto } from '@/services/types/api.types';
import { useConfigurationStore, useBlocksStore } from '@/stores';
import { useProjectStore } from '@/stores/projects.store';

import ReorderableTable from '@/components/ReorderableTable.vue';
import CreateModuleDialog from '@/components/dialogs/CreateModuleDialog.vue';
import { useI18n } from 'vue-i18n';
import { getTranslation, getModuleDisplayTitleWithFallback } from '@/composables/data-display-utils';
import { Constants } from '@/constants';
import { blockLabelsOf } from '@/composables/useBlockParent';
import type { BlockParent, BlockRouteFor } from '@/types/modules';

const $q = useQuasar();
const blocksStore = useBlocksStore();
const configurationStore = useConfigurationStore();
const projectStore = useProjectStore();

const { t } = useI18n();

// PROPS
const props = defineProps<{
  parent: BlockParent;
  blockRouteFor: BlockRouteFor;
  languageEnabled: Locales[];
}>();

// COMPUTED
// Only this parent's blocks: the store may still hold another parent's while this one's are fetched.
const blocks = computed(() => blocksStore.getBlocksByParent(props.parent));

// A Hero can show its project's title, which a page does not have. A page can share a project's id: the type decides.
const project = computed(() =>
  props.parent.type === Constants.Block.ParentType.Project ? projectStore.getItem(props.parent.id) : undefined,
);

// Or its value for a type the configuration does not name, never a blank.
const getTypeName = (row: ProjectModuleDto) =>
  getTranslation(
    moduleType.value.find((x) => x.value === row.block_type),
    'label',
    props.languageEnabled,
  ) || String(row.block_type);

// An untitled block is named by the type its row shows.
const getModuleTitle = (row: ProjectModuleDto) => {
  return getModuleDisplayTitleWithFallback(row, project.value, props.languageEnabled, getTypeName(row));
};

const columns = computed(() => [
  {
    name: 'title',
    label: t('global.label.title'),
    align: 'left',
    field: (row: ProjectModuleDto) => getModuleTitle(row),
  },
  {
    name: 'type',
    label: t('global.label.type'),
    field: (row: ProjectModuleDto) => getTypeName(row),
    align: 'left',
  },
]);

const moduleType = computed(() => configurationStore.getProjectModulesEnabled());

// LIFECYCLE
onMounted(async () => {
  await blocksStore.fetchBlocks(props.parent);
});

// FUNCTIONS
const handleAddRow = () => {
  const module: ProjectModuleDto = blocksStore.createModule(props.parent);
  $q.dialog({
    component: CreateModuleDialog,
    componentProps: {
      mode: 'add',
      module,
      languageEnabled: props.languageEnabled,
      parent: props.parent,
      blockRouteFor: props.blockRouteFor,
    },
  });
};

const handleDeleteRow = (row: ProjectModuleDto) => {
  // A project's first Hero shows the project's title and description, which the warning is about. A page has neither.
  const isProjectParent = props.parent.type === Constants.Block.ParentType.Project;
  const isFirstModule = blocks.value[0]?.id === row.id;
  const isHeroModule = row.block_type === Constants.Block.Type.Hero;

  let dialogTitle = t('global.dialogs.deleteTitle', { type: t('project-module.name') });
  let dialogMessage = t('global.dialogs.deleteMessage', { type: t('project-module.name') });

  if (isProjectParent && isFirstModule && isHeroModule) {
    dialogTitle = t('global.dialogs.deleteHeroModuleTitle');
    dialogMessage = t('global.dialogs.deleteHeroModuleMessage');
  }

  $q.dialog({
    title: dialogTitle,
    message: dialogMessage,
    cancel: true,
    persistent: true,
  }).onOk(async () => {
    await blocksStore.deleteModule(row);
  });
};

const handleRowClick = (row: ProjectModuleDto) => {
  router.push(props.blockRouteFor(row.id!));
};

const handleReorder = (source: ProjectModuleDto, target: ProjectModuleDto) => {
  blocksStore.updateModules(source, target);
};
</script>

<template>
  <div>
    <reorderable-table
      :rows="blocks"
      :columns="columns"
      :title="t(blockLabelsOf(parent.type).blocksKey)"
      itemKey="id"
      @add-row="handleAddRow"
      @row-click="handleRowClick"
      @delete-row="handleDeleteRow"
      @reorder="handleReorder"
    />
  </div>
</template>
